import { log } from '../../utils/consoleLogger';
import { PointOfView, productImageTemplate } from './constants';

/**
 * Extract the numeric index from an image filename
 * Supports patterns like: Object_9.jpg, Object_9_full.jpg, or 6_full-scaled.jpg
 * Returns the number before .jpg or before _full
 */
export const extractImageIndex = (filename, debug = false) => {
  if (!filename) return 0;
  
  // Remove path if present, get just the filename
  const lastSlashIndex = filename.lastIndexOf('/');
  const name = lastSlashIndex >= 0 ? filename.substring(lastSlashIndex + 1) : filename;
  
  let result = 0;
  
  // Handle full HD images (e.g., Object_9_full.jpg or 6_full-scaled.jpg)
  // Match numbers that come before _full
  if (name.includes('_full')) {
    const match = name.match(/(\d+)_full/);
    result = match ? parseInt(match[1], 10) : 0;
  } else {
    // Handle standard images (e.g., Object_9.jpg)
    const match = name.match(/(\d+)(?=\.jpg|\.jpeg|\.png|\.gif)/i);
    result = match ? parseInt(match[1], 10) : 0;
  }
  
  // Debug logging only when enabled
  if (debug) {
    log(`extractImageIndex: filename="${filename}", name="${name}", result=${result}`);
  }
  
  return result;
};

/**
 * Sort an array of image URLs by their numeric index
 * Ensures images are in the correct order (1, 2, 3, ...)
 */
export const sortImagesByIndex = (imageArray, debugName = '') => {
  if (!Array.isArray(imageArray) || imageArray.length === 0) {
    return imageArray;
  }
  
  const shouldDebug = false; //debugName.includes('HD'); // Only debug HD images for now
  
  // Log first 3 items before sorting
  if (shouldDebug && imageArray.length > 0) {
    log(`${debugName} - Unsorted array (first 3):`);
    imageArray.slice(0, 3).forEach((url, idx) => {
      const extractedIdx = extractImageIndex(url, false);
      log(`  [${idx}]: index=${extractedIdx}, url=${url}`);
    });
  }
  
  const sorted = [...imageArray].sort((a, b) => {
    const indexA = extractImageIndex(a, shouldDebug);
    const indexB = extractImageIndex(b, shouldDebug);
    
    return indexA - indexB;
  });
  
  if (shouldDebug && sorted.length > 0) {
    log(`${debugName} - Sorted result (first 10):`);
    sorted.slice(0, 10).forEach((url, idx) => {
      log(`  [${idx}]: index=${extractImageIndex(url)}, url=${url}`);
    });
  }
  
  return sorted;
};

/**
 * Initialize product settings from props
 */
export const initializeProductSettings = (productSettingsProp) => {
  try {
    if (typeof productSettingsProp === 'string') {
      // Avoid parsing "[object Object]" string explicitly
      if (productSettingsProp === '[object Object]') {
        console.warn('Received "[object Object]" string for productSettings, defaulting to empty object.');
        return {};
      }
      // Ensure there's a string to parse, default to '{}' if props.productSettings is null, undefined, or empty
      return JSON.parse(productSettingsProp || '{}');
    } else if (typeof productSettingsProp === 'object' && productSettingsProp !== null) {
      log('Main.js - product settings from props:', productSettingsProp);
      return productSettingsProp; // It's already an object
    }
  } catch (error) {
    console.error("Error parsing props.productSettings:", error);
  }
  // Default to an empty object if type is unexpected or parsing fails
  return {};
};

/**
 * Initialize assets URLs from props
 */
export const initializeAssetsUrls = (assetsUrlsProp) => {
  try {
    if (typeof assetsUrlsProp === 'string') {
      // Avoid parsing "[object Object]" string explicitly
      if (assetsUrlsProp === '[object Object]') {
        console.warn('Received "[object Object]" string for assetsUrls, defaulting to empty object.');
        return {};
      }
      return JSON.parse(assetsUrlsProp || '{}'); // Use empty object string as default if null/undefined
    } else if (typeof assetsUrlsProp === 'object' && assetsUrlsProp !== null) {
      log('Main.js - Assets URLs from props:', assetsUrlsProp);
      return assetsUrlsProp; // It's already an object
    }
  } catch (error) {
    console.error("Error parsing props.assetsUrls:", error);
  }
  // Default to an empty object if type is unexpected or parsing fails
  return {};
};

/**
 * Calculate aspect ratio for a single image
 */
export const calculateImageAspectRatio = (imageUrl) => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.src = imageUrl;
    
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      resolve(width / height);
    };
    
    img.onerror = () => {
      console.warn('Failed to load image for aspect ratio calculation:', imageUrl);
      resolve(null);
    };
  });
};

/**
 * Calculate aspect ratios for multiple images
 */
export const calculateAspectRatios = async ({ image360, productImage, topImage }) => {
  log('Main.js: Image for 360 ratio:', image360);
  log('Main.js: Image for product ratio:', productImage);
  log('Main.js: Image for top ratio:', topImage);

  const [ratio360, ratioProduct, ratioTop] = await Promise.all([
    calculateImageAspectRatio(image360),
    calculateImageAspectRatio(productImage),
    calculateImageAspectRatio(topImage),
  ]);

  return {
    aspectRatio360: ratio360,
    aspectRatioProduct: ratioProduct,
    aspectRatioTop: ratioTop,
    is360Present: ratio360 !== null,
    isProductPresent: ratioProduct !== null,
    isTopPresent: ratioTop !== null,
  };
};

/**
 * Apply product settings to component state
 */
export const applyProductSettings = (settingsData, setters) => {
  const settings = typeof settingsData === 'string' ? JSON.parse(settingsData || '{}') : (settingsData || {});
  log('start pitch angle:', settings.startCamPitchAngle, 'yaw angle:', settings.startCamYawAngle);
  
  setters.setYawAngle(settings.startCamYawAngle || 15);
  setters.setPitchAngle(settings.startCamPitchAngle || 75);
  setters.setWindowMargin(settings.windowMargin !== undefined ? settings.windowMargin : 10);
  setters.setViewerMarginLeft(settings.viewerMarginLeft !== undefined ? settings.viewerMarginLeft : 0);
  setters.setViewerMarginRight(settings.viewerMarginRight !== undefined ? settings.viewerMarginRight : 0);
  setters.setViewerMarginBottom(settings.viewerMarginBottom !== undefined ? settings.viewerMarginBottom : 0);
  setters.setViewerContainerMarginLeft(settings.viewerContainerMarginLeft !== undefined ? settings.viewerContainerMarginLeft : 0);
  setters.setViewerContainerMarginRight(settings.viewerContainerMarginRight !== undefined ? settings.viewerContainerMarginRight : 0);
  setters.setViewerContainerMarginBottom(settings.viewerContainerMarginBottom !== undefined ? settings.viewerContainerMarginBottom : 0);
  setters.setWidthSwitchToFullScreen(settings.switchToFullScreen !== undefined ? settings.switchToFullScreen : 980);
  setters.setMaxViewerWidth(settings.maxViewerWidth !== undefined ? settings.maxViewerWidth : 1100);
};
